import { useQuery } from "@tanstack/react-query"
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts"
import { motion } from "framer-motion"
import {
  TrendingUp,
  Calendar,
  Activity,
  Clock,
} from "lucide-react"
import { KPICard } from "./KPICard"
import { ChartCard } from "./ChartCard"
import { fetchTimelineData } from "../api"
import type { DashboardFilters } from "../DashboardPage"

function formatWeekLabel(week: string): string {
  const dt = new Date(week + "T00:00:00")
  return `${dt.getDate()}/${dt.getMonth() + 1}`
}

function formatWeekFull(week: string): string {
  const dt = new Date(week + "T00:00:00")
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

interface TimelineTabProps {
  filters: DashboardFilters
}

export function TimelineTab({ filters }: TimelineTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "timeline", filters],
    queryFn: () =>
      fetchTimelineData({
        campaign_status: filters.campaignStatus,
        market: filters.market || undefined,
      }),
    staleTime: 2 * 60_000,
  })

  const overview = data?.overview
  const goLiveTrend = (data?.go_live_trend ?? []).map((d) => ({
    ...d,
    label: formatWeekLabel(d.week),
  }))
  const activityTrend = (data?.activity_trend ?? []).map((d) => ({
    ...d,
    label: formatWeekLabel(d.week),
  }))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
      </div>
    )
  }

  return (
    <div
      className="flex flex-col p-6 gap-5 overflow-hidden"
      style={{ height: "100%" }}
    >
      {/* KPI Row */}
      <div key={`kpis-${filters.animationKey}`} className="grid grid-cols-4 gap-4 flex-shrink-0">
        {[
          <KPICard
            key="week"
            icon={TrendingUp}
            label="Gone Live This Week"
            value={(overview?.gone_live_this_week ?? 0).toLocaleString()}
            subtext="Campaigns launched this week"
          />,
          <KPICard
            key="month"
            icon={Calendar}
            label="Gone Live This Month"
            value={(overview?.gone_live_this_month ?? 0).toLocaleString()}
            subtext="Campaigns launched this month"
          />,
          <KPICard
            key="events"
            icon={Activity}
            label="Total Events"
            value={(overview?.total_events ?? 0).toLocaleString()}
            subtext="All recorded activity"
            tooltip="Total campaign events including uploads, approvals, comments, and status changes"
          />,
          <KPICard
            key="days"
            icon={Clock}
            label="Avg Days to Live"
            value={overview?.avg_days_to_live != null ? `${overview.avg_days_to_live}` : "—"}
            subtext={overview?.live_sample_size ? `Based on ${overview.live_sample_size} campaigns` : "No data yet"}
            tooltip="Average number of days from campaign creation to going live"
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.06, ease: [0.4, 0, 0.2, 1] }}
          >
            {card}
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="flex-1 min-h-0 flex gap-5">
        {/* Go-Live Trend */}
        <div className="flex-[3]">
          <ChartCard
            icon={TrendingUp}
            title="Go-Live Trend"
            subtitle="Campaigns going live per week"
          >
            {goLiveTrend.length > 0 ? (
              <ResponsiveContainer key={`golive-${filters.animationKey}`} width="100%" height="100%">
                <AreaChart data={goLiveTrend} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <defs>
                    <linearGradient id="goLiveGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e40af" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1e40af" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" strokeWidth={1} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const entry = payload[0].payload as { week: string; count: number }
                      return (
                        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600">
                          <p className="font-semibold mb-1">Week of {formatWeekFull(entry.week)}</p>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Went live:</span>
                            <span className="font-mono font-medium">{entry.count} campaign{entry.count !== 1 ? "s" : ""}</span>
                          </div>
                        </div>
                      )
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#1e40af"
                    strokeWidth={2}
                    fill="url(#goLiveGradient)"
                    dot={{ r: 4, fill: "#1e40af", strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: "#1e40af", strokeWidth: 2, stroke: "#dbeafe" }}
                    animationBegin={200}
                    animationDuration={800}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No go-live data yet
              </div>
            )}
          </ChartCard>
        </div>

        {/* Activity Volume */}
        <div className="flex-[2]">
          <ChartCard
            icon={Activity}
            title="Activity Volume"
            subtitle="Campaign events per week"
          >
            {activityTrend.length > 0 ? (
              <ResponsiveContainer key={`activity-${filters.animationKey}`} width="100%" height="100%">
                <BarChart data={activityTrend} margin={{ top: 8, right: 8, left: -8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" strokeWidth={1} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const entry = payload[0].payload as { week: string; count: number }
                      return (
                        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600">
                          <p className="font-semibold mb-1">Week of {formatWeekFull(entry.week)}</p>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Events:</span>
                            <span className="font-mono font-medium">{entry.count.toLocaleString()}</span>
                          </div>
                        </div>
                      )
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#93c5fd"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    animationBegin={200}
                    animationDuration={800}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No activity data yet
              </div>
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  )
}
